import React, { useState } from 'react';
import { Plus, Users } from 'lucide-react';
import { AppChrome, BrandLogo } from './AppChrome';
import { SectionIntro } from './SectionIntro';

/**
 * The claim of this pillar is oversight: one place that shows which company
 * tools are reachable, and which AI surfaces can reach them. A catalogue of
 * 4,000 logos does not say that — a map of tool → surface does.
 */

type SurfaceId = 'web' | 'slack' | 'chatgpt' | 'claude' | 'grok';

// Third-party surfaces first, Giga's own last: the list reads as "everywhere
// else, plus the two places Giga itself runs".
const SURFACES: Array<{
  id: SurfaceId;
  name: string;
  slug: string;
  note: string;
}> = [{
  id: 'chatgpt',
  name: 'ChatGPT',
  slug: 'chatgpt',
  note: 'Via Giga connector'
}, {
  id: 'claude',
  name: 'Claude Code',
  slug: 'claude',
  note: 'Engineering only'
}, {
  id: 'grok',
  name: 'Grok bot',
  slug: 'grok',
  note: 'Growth only'
}, {
  // Giga is listed as one surface, not as "for web" and "for Slack" — the
  // point is that it sits at the same level as the third-party surfaces.
  id: 'web',
  name: 'Giga',
  slug: 'giga',
  note: 'Company-wide'
}];
/**
 * Every mark in this screen resolves through one component, so optical sizing
 * (ChatGPT's knot in particular) and Giga's own mark are handled in a single
 * place. The previous special case forced Giga's mark to pure black with a
 * brightness filter, which flattened it against brand-coloured logos.
 */
function SurfaceIcon({
  slug,
  className,
  dark = false
}: {
  slug: string;
  className: string;
  dark?: boolean;
}) {
  // FULL COLOUR HERE, deliberately. The flattening rule earns its keep in the
  // 28-mark directory band, where unrelated corporate colour reads as a
  // collage. On this screen the marks ARE the content — which tool, which
  // surface — so recognition beats uniformity, and the silhouettes had turned
  // eleven distinct products into eleven identical grey tiles.
  return <BrandLogo slug={slug} onDark={dark} className={className} data-id="element-720" data-mp="h8h3w79hs1il" />;
}
type Node = {
  slug: string;
  name: string;
  accounts: number;
  surfaces: SurfaceId[];
};

// Workspace-wide view: every connected tool, how many accounts sit behind it,
// and which AI surfaces are allowed to use it.
const BY_SURFACE: Node[] = [{
  slug: 'slack',
  name: 'Slack',
  accounts: 3,
  surfaces: ['chatgpt', 'claude', 'web', 'slack']
}, {
  slug: 'gmail',
  name: 'Gmail',
  accounts: 5,
  surfaces: ['chatgpt', 'web', 'slack']
}, {
  slug: 'intercom',
  name: 'Intercom',
  accounts: 4,
  surfaces: ['chatgpt', 'web', 'slack']
}, {
  slug: 'salesforce',
  name: 'Salesforce',
  accounts: 2,
  surfaces: ['chatgpt', 'grok', 'web']
}, {
  slug: 'linear',
  name: 'Linear',
  accounts: 3,
  surfaces: ['claude', 'web', 'slack']
}, {
  slug: 'snowflake',
  name: 'Snowflake',
  accounts: 1,
  surfaces: ['claude', 'web']
}, {
  slug: 'hubspot',
  name: 'HubSpot',
  accounts: 2,
  surfaces: ['grok', 'web', 'slack']
}];
type Member = {
  name: string;
  role: string;
  initials: string;
  tint: string;
  nodes: Node[];
};
const TEAM: Member[] = [{
  name: 'Sarah Chen',
  role: 'Head of Ops · Admin',
  initials: 'SC',
  tint: '#134E4A',
  nodes: [{
    slug: 'slack',
    name: 'Slack',
    accounts: 1,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'gmail',
    name: 'Gmail',
    accounts: 2,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'salesforce',
    name: 'Salesforce',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'snowflake',
    name: 'Snowflake',
    accounts: 1,
    surfaces: ['chatgpt', 'web']
  }]
}, {
  name: 'Marcus Lee',
  role: 'Engineering',
  initials: 'ML',
  tint: '#3F3F46',
  nodes: [{
    slug: 'linear',
    name: 'Linear',
    accounts: 1,
    surfaces: ['claude', 'web', 'slack']
  }, {
    slug: 'github',
    name: 'GitHub',
    accounts: 2,
    surfaces: ['claude', 'web']
  }, {
    slug: 'sentry',
    name: 'Sentry',
    accounts: 1,
    surfaces: ['claude', 'slack']
  }, {
    slug: 'slack',
    name: 'Slack',
    accounts: 1,
    surfaces: ['web', 'slack']
  }]
}, {
  name: 'Priya Raman',
  role: 'Growth',
  initials: 'PR',
  tint: '#7C2D12',
  nodes: [{
    slug: 'hubspot',
    name: 'HubSpot',
    accounts: 2,
    surfaces: ['grok', 'web', 'slack']
  }, {
    slug: 'mailchimp',
    name: 'Mailchimp',
    accounts: 1,
    surfaces: ['grok', 'web']
  }, {
    slug: 'ahrefs',
    name: 'Ahrefs',
    accounts: 1,
    surfaces: ['grok', 'chatgpt']
  }, {
    slug: 'slack',
    name: 'Slack',
    accounts: 1,
    surfaces: ['web', 'slack']
  }]
}, {
  name: 'Tom Okafor',
  role: 'Finance',
  initials: 'TO',
  tint: '#1E3A5F',
  nodes: [{
    slug: 'stripe',
    name: 'Stripe',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'quickbooks',
    name: 'QuickBooks',
    accounts: 1,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'gmail',
    name: 'Gmail',
    accounts: 1,
    surfaces: ['chatgpt', 'web']
  }]
}, {
  name: 'Dana Whitfield',
  role: 'Support',
  initials: 'DW',
  tint: '#4C1D95',
  nodes: [{
    slug: 'intercom',
    name: 'Intercom',
    accounts: 2,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'linear',
    name: 'Linear',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'slack',
    name: 'Slack',
    accounts: 1,
    surfaces: ['web', 'slack']
  }]
}];
// The middle cut of the same map. A person's connections answer "what can
// Sarah reach"; a group's answer "what can this team reach" — which is the
// question an admin actually asks when granting or revoking access, since
// access is granted by group rather than person by person.
type Group = {
  name: string;
  color: string;
  members: number;
  nodes: Node[];
};
const GROUPS: Group[] = [{
  name: 'Sales',
  color: '#134E4A',
  members: 7,
  nodes: [{
    slug: 'salesforce',
    name: 'Salesforce',
    accounts: 2,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'gmail',
    name: 'Gmail',
    accounts: 3,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'slack',
    name: 'Slack',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'granola',
    name: 'Granola',
    accounts: 2,
    surfaces: ['web', 'slack']
  }]
}, {
  name: 'Marketing',
  color: '#7C2D12',
  members: 6,
  nodes: [{
    slug: 'hubspot',
    name: 'HubSpot',
    accounts: 2,
    surfaces: ['grok', 'web', 'slack']
  }, {
    slug: 'mailchimp',
    name: 'Mailchimp',
    accounts: 1,
    surfaces: ['grok', 'web']
  }, {
    slug: 'ahrefs',
    name: 'Ahrefs',
    accounts: 1,
    surfaces: ['grok', 'chatgpt']
  }, {
    slug: 'figma',
    name: 'Figma',
    accounts: 2,
    surfaces: ['web', 'slack']
  }]
}, {
  name: 'Customer Success',
  color: '#3F3A2E',
  members: 4,
  nodes: [{
    slug: 'intercom',
    name: 'Intercom',
    accounts: 4,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'linear',
    name: 'Linear',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'salesforce',
    name: 'Salesforce',
    accounts: 1,
    surfaces: ['web', 'slack']
  }]
}, {
  name: 'Engineering',
  color: '#3F3F46',
  members: 9,
  nodes: [{
    slug: 'github',
    name: 'GitHub',
    accounts: 2,
    surfaces: ['claude', 'web']
  }, {
    slug: 'linear',
    name: 'Linear',
    accounts: 3,
    surfaces: ['claude', 'web', 'slack']
  }, {
    slug: 'sentry',
    name: 'Sentry',
    accounts: 1,
    surfaces: ['claude', 'slack']
  }, {
    slug: 'snowflake',
    name: 'Snowflake',
    accounts: 1,
    surfaces: ['claude', 'web']
  }]
}, {
  name: 'Leadership',
  color: '#1E3A5F',
  members: 5,
  nodes: [{
    slug: 'stripe',
    name: 'Stripe',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'quickbooks',
    name: 'QuickBooks',
    accounts: 1,
    surfaces: ['chatgpt', 'web']
  }, {
    slug: 'salesforce',
    name: 'Salesforce',
    accounts: 1,
    surfaces: ['chatgpt', 'web']
  }, {
    slug: 'snowflake',
    name: 'Snowflake',
    accounts: 1,
    surfaces: ['web']
  }]
}];
/*
 * Three headings that read as a matched set — similar length, each stating an
 * outcome rather than a feature. The first one's job is the N-vs-N×M argument:
 * without Giga the integration work repeats for every AI product a company
 * adopts, so its body copy now carries the multiplication ("one connection
 * serves every AI surface") instead of only the mechanism.
 */
const POINTS = [{
  title: 'Connect once, not per tool',
  copy: 'Credentials live in Giga, not inside ChatGPT or Claude, so one connection serves every AI surface — and adding another one costs you nothing.'
}, {
  title: 'Governed, not guessed',
  copy: 'Every connection has an owner, a group, and a history. Admins can see what is connected and where company data can flow.'
}, {
  title: 'Stays connected on its own',
  copy: 'Expiring logins are renewed for you, and if a connection ever does drop, the person who owns it gets a one-click link to restore it.'
}];
/**
 * The Connections screen itself, without the section around it. Split out so
 * the export page can render exactly the same screen standalone.
 */
export function ConnectionsScreen({
  dark = false
}: {
  /**
   * Dark chrome, light content. Only the frame — heading, counts, tabs, column
   * labels — recedes; the tool and surface rows stay white, because they are
   * the evidence and must not lose their footing.
   */
  dark?: boolean;
} = {}) {
  const [tab, setTab] = useState<'surface' | 'group' | 'person'>('surface');
  const [memberIndex, setMemberIndex] = useState(0);
  const [groupIndex, setGroupIndex] = useState(0);
  const nodes = tab === 'surface' ? BY_SURFACE : tab === 'group' ? GROUPS[groupIndex].nodes : TEAM[memberIndex].nodes;
  const surfaces = tab === 'surface' ? SURFACES : SURFACES.filter(s => nodes.some(n => n.surfaces.includes(s.id)));
  const accounts = nodes.reduce((sum, n) => sum + n.accounts, 0);
  return <div className="px-6 py-6 md:px-9 md:py-8" data-id="element-721" data-mp="1y1grw8idi0jx">
      <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-722" data-mp="3ny0j0j8ewipd">
        <div data-id="element-723" data-mp="3nzbii6utr4ao">
          <h3 className={`font-title text-[24px] font-semibold leading-tight tracking-[-0.015em] transition-colors duration-700 ease-out ${dark ? 'text-white' : 'text-[#2B211A]'}`} data-id="element-724" data-mp="3j71yf7ge4ygb">
            Connections
          </h3>
          {/* Counts as discrete facts rather than one run-on line — easier to
           scan, and the separators stop reading as punctuation. */}
          <p className={`mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12.5px] tabular-nums transition-colors duration-700 ease-out ${dark ? 'text-white/55' : 'text-neutral-500'}`} data-id="element-725" data-mp="aow2am3rxopd">
            <span data-id="element-726" data-mp="uvqk1dzll7o">
              <strong className={`font-semibold ${dark ? 'text-white' : 'text-[#2B211A]'}`} data-id="element-727" data-mp="3swlkelzeh8fe">
                {nodes.length}
              </strong>{' '}
              tools
            </span>
            <span className={dark ? 'text-white/25' : 'text-[#E2DACD]'} data-id="element-728" data-mp="2lgox1htkb57t">·</span>
            <span data-id="element-729" data-mp="2hg512hkq67n9">
              <strong className={`font-semibold ${dark ? 'text-white' : 'text-[#2B211A]'}`} data-id="element-730" data-mp="2hjssnb6m0jzc">
                {accounts}
              </strong>{' '}
              accounts
            </span>
            <span className={dark ? 'text-white/25' : 'text-[#E2DACD]'} data-id="element-731" data-mp="10ywjvdkj77r8">·</span>
            <span data-id="element-732" data-mp="15yiy9qc3iu1m">
              <strong className={`font-semibold ${dark ? 'text-white' : 'text-[#2B211A]'}`} data-id="element-733" data-mp="15xze02yz3tkf">
                {surfaces.length}
              </strong>{' '}
              AI surfaces
            </span>
          </p>
        </div>
        {/* On the dark frame the ink button disappears into the panel, so the
         action takes the brand orange — the one value that still reads. */}
        <span className={`inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-[12.5px] font-bold text-white shadow-[0_1px_2px_rgba(43,33,26,0.3),0_8px_18px_-10px_rgba(43,33,26,0.7)] transition-colors duration-700 ease-out ${dark ? 'bg-[#EA580C]' : 'bg-[#2B211A]'}`} data-id="element-734" data-mp="1or3d43uymymf">
          <Plus size={13} strokeWidth={2.6} data-id="element-735" data-mp="3vi2p0km5xqiu" />
          Connect new tool
        </span>
      </div>

      <div className={`mb-6 mt-7 flex items-center gap-6 border-b transition-colors duration-700 ease-out ${dark ? 'border-white/15' : 'border-[#EFEAE1]'}`} data-id="element-736" data-mp="12nkmjn0u2bs6">
        {/* Widest cut first, then narrowing: everything → a team → one
         person. */}
        {([['surface', 'By surface'], ['group', 'By group'], ['person', 'By person']] as const).map(([id, label]) => <button key={id} type="button" onClick={() => setTab(id)} className={`-mb-px border-b-2 pb-3 text-[13px] font-semibold transition-colors ${tab === id ? dark ? 'border-white text-white' : 'border-[#2B211A] text-[#2B211A]' : dark ? 'border-transparent text-white/45 hover:text-white' : 'border-transparent text-neutral-400 hover:text-[#2B211A]'}`} data-id="element-737" data-mp="1llf1sisk1jj0">
              {label}
            </button>)}
      </div>

      {/* Group and person are the same map with a filter on top, not
       different screens — so the mental model carries across all three tabs.
       Group tiles are squared and carry the people glyph, matching how a group
       is drawn on the Groups screen. */}
      {tab === 'group' && <div className="mb-4 flex flex-wrap gap-1.5" data-id="element-738" data-mp="2nxew7lhh6wvm">
          {GROUPS.map((g, i) => <button key={g.name} type="button" onClick={() => setGroupIndex(i)} aria-pressed={i === groupIndex} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-semibold transition-colors ${i === groupIndex ? 'border-[#2B211A] bg-[#2B211A] text-white' : 'border-[#E7E0D4] text-[#4A3E35] hover:border-[#2B211A]/35'}`} data-id="element-739" data-mp="2nvc7r5vz1bt0" data-mpi={i}>
              <span className="flex size-4 items-center justify-center rounded-[5px]" style={{
          backgroundColor: i === groupIndex ? 'rgba(255,255,255,0.22)' : g.color
        }} data-id="element-740" data-mp="24zm31awnm1bf">
                <Users size={9} strokeWidth={2.4} className="text-white" data-id="element-741" data-mp="1va2im0talzeg" />
              </span>
              {g.name}
              <span className={`tabular-nums ${i === groupIndex ? 'text-white/55' : 'text-neutral-400'}`} data-id="element-742" data-mp="1h9rqyvk5sgs8">
                {g.members}
              </span>
            </button>)}
        </div>}

      {tab === 'person' && <div className="mb-4 flex flex-wrap gap-1.5" data-id="element-743" data-mp="1e6vddg522kx5">
          {TEAM.map((m, i) => <button key={m.name} type="button" onClick={() => setMemberIndex(i)} aria-pressed={i === memberIndex} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-semibold transition-colors ${i === memberIndex ? 'border-[#2B211A] bg-[#2B211A] text-white' : 'border-[#E7E0D4] text-[#4A3E35] hover:border-[#2B211A]/35'}`} data-id="element-744" data-mp="199v7p6c4bk6k" data-mpi={i}>
              <span className="flex size-4 items-center justify-center rounded-full text-[8px] font-bold text-white" style={{
          backgroundColor: i === memberIndex ? 'rgba(255,255,255,0.22)' : m.tint
        }} data-id="element-745" data-mp="qic8zbp4msac">
                {m.initials}
              </span>
              {m.name.split(' ')[0]}
            </button>)}
        </div>}

      <AccessMap key={tab === 'surface' ? 'all' : tab === 'group' ? `g-${GROUPS[groupIndex].name}` : TEAM[memberIndex].name} nodes={nodes} surfaces={surfaces} dark={dark} data-id="element-746" data-mp="2fvpc4xli6a9v" />
    </div>;
}

/**
 * The four supporting points. These are read, not looked at — dense, specific
 * copy that was being lost against a moving photograph inside the camera
 * scene. On cream, above the comparison table, they get a still ground and
 * proper contrast, and they set up the table that follows.
 */
export function ConnectedAppsPoints({
  dark = false
}: {
  dark?: boolean;
} = {}) {
  return <SectionIntro className="w-full" align="left" data-id="element-747" data-mp="20gu3er9skqxg">
      {/* Three across, not a 2×2 with a hole in it: with the coverage point
       promoted into the section's lead, an even grid would leave one cell
       empty and read as something missing.
        NO ICONS, and a rule instead. Three bordered icon plates carrying a key,
       an eye and an arrow added nothing the headings do not already say — they
       were three pieces of chrome competing with the words for the start of
       each column. A single hairline above each point does the grouping job
       instead: it states the column structure, aligns all three headings on one
       optical baseline, and is the quietest possible way to do both. */}
      <div className="grid gap-x-12 gap-y-10 md:grid-cols-3" data-id="element-748" data-mp="2v6yqa7f318h1">
        {POINTS.map(({
        title,
        copy
      }) => <div key={title} className={`border-t pt-5 ${dark ? 'border-white/15' : 'border-[#2B211A]/12'}`} data-id="element-749" data-mp="3a499k2xyvinb">
            <h3 className={`font-title whitespace-nowrap text-[21px] font-bold leading-none tracking-tight ${dark ? 'text-white' : 'text-[#2B211A]'}`} data-id="element-750" data-mp="353uzkfct1ymn">
              {title}
            </h3>
            <p className={`mt-3.5 text-[15px] leading-relaxed ${dark ? 'text-[#C9BFB4]' : 'text-[#6B5D52]'}`} data-id="element-751" data-mp="2cj3g0xioicso">
              {copy}
            </p>
          </div>)}
      </div>
    </SectionIntro>;
}
// Taller rows, wider gutter. The map's job is oversight, and oversight reads as
// calm — a tight grid of rows reads as a database table instead.
const ROW_H = 60;
const ROW_GAP = 10;
const STEP = ROW_H + ROW_GAP;
const CONNECTOR_W = 132;
const LABEL_H = 26;
type Focus = {
  kind: 'tool';
  slug: string;
} | {
  kind: 'surface';
  id: SurfaceId;
} | null;

/** Tools on the left, AI surfaces on the right, lines for what reaches what. */
function AccessMap({
  nodes,
  surfaces,
  dark = false
}: {
  nodes: Node[];
  surfaces: Array<(typeof SURFACES)[number]>;
  dark?: boolean;
}) {
  const [focus, setFocus] = useState<Focus>(null);
  const listH = (n: number) => n * STEP - ROW_GAP;
  const height = Math.max(listH(nodes.length), listH(surfaces.length));
  const centerY = (index: number, count: number) => (height - listH(count)) / 2 + index * STEP + ROW_H / 2;
  const isLive = (slug: string, id: SurfaceId) => {
    if (!focus) return true;
    if (focus.kind === 'tool') return focus.slug === slug;
    return focus.id === id;
  };
  const toolDim = (slug: string) => focus?.kind === 'tool' ? focus.slug !== slug : focus?.kind === 'surface' ? !nodes.find(n => n.slug === slug)?.surfaces.includes(focus.id) : false;
  const surfaceDim = (id: SurfaceId) => focus?.kind === 'surface' ? focus.id !== id : focus?.kind === 'tool' ? !nodes.find(n => n.slug === focus.slug)?.surfaces.includes(id) : false;
  /*
   * THE ROWS ON THE OTHER SIDE ARE MARKED, NOT MERELY LEFT ALONE.
   *
   * The connector lines are drawn between the two columns, which only exist
   * side by side — stacked on a phone there is no gutter to draw them in, so
   * the only feedback a tap produced was everything unrelated fading. Fading
   * reads as "things went away", not as "these two are linked". A positive
   * accent on the counterpart rows states the relationship directly, which is
   * the whole claim: this tool is reachable from exactly these surfaces.
   */
  const linked = (dim: boolean, self: boolean) => Boolean(focus) && !dim && !self;
  const LINK_RING = dark ? 'ring-1 ring-[#FB923C]/45' : 'ring-1 ring-[#EA580C]/35';
  return <div className="flex flex-col gap-4 md:flex-row md:items-stretch md:gap-0" data-id="element-752" data-mp="15oukzn3rs65s">
      <ul className="flex-1 space-y-2.5" onMouseLeave={() => setFocus(null)} data-id="element-753" data-mp="10u8arn9fcjna">
        <ListLabel dark={dark} offset={(height - listH(nodes.length)) / 2} data-id="element-754" data-mp="1890ra1fuw8pi">
          Connected tools
        </ListLabel>
        {nodes.map(n => <li key={n.slug} data-id="element-755" data-mp="1d8dmpaksmo3p">
            <button type="button" onMouseEnter={() => setFocus({
          kind: 'tool',
          slug: n.slug
        })} onFocus={() => setFocus({
          kind: 'tool',
          slug: n.slug
        })} onClick={() => setFocus(f => f?.kind === 'tool' && f.slug === n.slug ? null : {
          kind: 'tool',
          slug: n.slug
        })} className={`flex w-full items-center gap-3.5 rounded-xl border px-4 text-left transition-[background-color,border-color,box-shadow,opacity] duration-200 ease-out ${focus?.kind === 'tool' && focus.slug === n.slug ? dark ? 'border-[#FB923C]/50 bg-[#FB923C]/[0.14]' : 'border-[#EA580C]/45 bg-[#FFF8F3] shadow-[0_2px_10px_-4px_rgba(234,88,12,0.28)]' : dark ? 'border-white/10 bg-white/[0.05]' : 'border-[#EFEAE1] bg-white shadow-[0_1px_2px_-1px_rgba(43,33,26,0.06)]'} ${toolDim(n.slug) ? 'opacity-35' : ''} ${linked(toolDim(n.slug), focus?.kind === 'tool' && focus.slug === n.slug) ? LINK_RING : ''}`} style={{
          height: ROW_H
        }} data-id="element-756" data-mp="1i541lukv3dnj">
              {/* The logo gets its own tile so wildly different marks — square
               favicons, round ones, wordmarks — sit on a common footprint. */}
              <span className={`flex size-9 shrink-0 items-center justify-center rounded-lg border ${dark ? 'border-white/10 bg-white/[0.06]' : 'border-[#F1ECE3] bg-[#FCFAF6]'}`} data-id="element-757" data-mp="3tq6r3xvsawkj">
                <BrandLogo slug={n.slug} onDark={dark} className="size-[18px]" data-id="element-758" data-mp="7jic90okyme5" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-759" data-mp="ccbenhwktin2">
                <span className={`block truncate text-[13.5px] font-semibold ${dark ? 'text-[#FAF8F4]' : 'text-[#2B211A]'}`} data-id="element-760" data-mp="gd52kba0qxfy">
                  {n.name}
                </span>
                <span className={`block truncate text-[11.5px] tabular-nums ${dark ? 'text-white/45' : 'text-neutral-500'}`} data-id="element-761" data-mp="lan1d8ubnwa8">
                  {n.accounts} connected{' '}
                  {n.accounts === 1 ? 'account' : 'accounts'}
                </span>
              </span>
              {/* Only the third-party surfaces are listed here. Giga itself can
               always reach every connection, so its two marks added no
               information — and at this size they read as black dots. */}
              <span className="flex shrink-0 items-center -space-x-1.5" data-id="element-762" data-mp="mi2tzywgxlc6">
                {n.surfaces.map(id => SURFACES.find(s => s.id === id)).filter((s): s is (typeof SURFACES)[number] => Boolean(s) && s!.slug !== 'giga').map(s => <SurfaceIcon key={s.id} slug={s.slug} dark={dark} className={`size-[22px] rounded-full p-[3px] ring-1 ${dark ? 'bg-[#2A211C] ring-white/15' : 'bg-white ring-[#EFEAE1]'}`} data-id="element-763" data-mp="19fxp8jqxtcc6" />)}
              </span>
            </button>
          </li>)}
      </ul>

      {/* Fixed row heights let the connectors be drawn from indices, so no
       measurement pass is needed and the lines never drift. */}
      <div className="hidden shrink-0 md:block" style={{
      width: CONNECTOR_W
    }} aria-hidden="true" data-id="element-764" data-mp="3rdvzy3s6xm6l">
        <div style={{
        height: LABEL_H + ROW_GAP
      }} data-id="element-765" data-mp="7uh391zhoae" />
        <svg width={CONNECTOR_W} height={height} viewBox={`0 0 ${CONNECTOR_W} ${height}`} data-id="element-766" data-mp="95kh7riou0z">
          {nodes.map((n, ti) => n.surfaces.map(id => {
          const si = surfaces.findIndex(s => s.id === id);
          if (si < 0) return null;
          const y1 = centerY(ti, nodes.length);
          const y2 = centerY(si, surfaces.length);
          // Nothing is drawn at rest — every link at once was noise. Lines
          // appear only for the row under the pointer.
          const live = Boolean(focus) && isLive(n.slug, id);
          return <path key={`${n.slug}-${id}`} d={`M0 ${y1} C ${CONNECTOR_W * 0.45} ${y1}, ${CONNECTOR_W * 0.55} ${y2}, ${CONNECTOR_W} ${y2}`} fill="none" stroke="#EA580C" strokeWidth={1.5} strokeLinecap="round" opacity={live ? 1 : 0} style={{
            transition: 'opacity 160ms ease-out'
          }} data-id="element-767" data-mp="33n8dmdhj2u0i" />;
        }))}
        </svg>
      </div>

      <ul className="space-y-2.5 md:w-[232px]" onMouseLeave={() => setFocus(null)} data-id="element-768" data-mp="2mez0knjkrbqx">
        <ListLabel dark={dark} offset={(height - listH(surfaces.length)) / 2} data-id="element-769" data-mp="2hh79twuo1vob">
          AI surfaces
        </ListLabel>
        {surfaces.map(s => <li key={s.id} data-id="element-770" data-mp="2rdshkpy2lkvu">
            <button type="button" onMouseEnter={() => setFocus({
          kind: 'surface',
          id: s.id
        })} onFocus={() => setFocus({
          kind: 'surface',
          id: s.id
        })} onClick={() => setFocus(f => f?.kind === 'surface' && f.id === s.id ? null : {
          kind: 'surface',
          id: s.id
        })} className={`flex w-full items-center gap-3.5 rounded-xl border px-4 text-left transition-[background-color,border-color,box-shadow,opacity] duration-200 ease-out ${focus?.kind === 'surface' && focus.id === s.id ? dark ? 'border-[#FB923C]/50 bg-[#FB923C]/[0.14]' : 'border-[#EA580C]/45 bg-[#FFF8F3] shadow-[0_2px_10px_-4px_rgba(234,88,12,0.28)]' : dark ? 'border-white/10 bg-white/[0.05]' : 'border-[#EFEAE1] bg-white shadow-[0_1px_2px_-1px_rgba(43,33,26,0.06)]'} ${surfaceDim(s.id) ? 'opacity-35' : ''} ${linked(surfaceDim(s.id), focus?.kind === 'surface' && focus.id === s.id) ? LINK_RING : ''}`} style={{
          height: ROW_H
        }} data-id="element-771" data-mp="2rdinor9wmtkv">
              <span className={`flex size-9 shrink-0 items-center justify-center rounded-lg border ${dark ? 'border-white/10 bg-white/[0.06]' : 'border-[#F1ECE3] bg-[#FCFAF6]'}`} data-id="element-772" data-mp="1w58d9vuwam0k">
                <SurfaceIcon slug={s.slug} dark={dark} className="size-[18px]" data-id="element-773" data-mp="13g1482xtee12" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-774" data-mp="18f4cdw93d0d9">
                <span className={`block truncate text-[13.5px] font-semibold ${dark ? 'text-[#FAF8F4]' : 'text-[#2B211A]'}`} data-id="element-775" data-mp="18eceufvxoskc">
                  {s.name}
                </span>
                <span className={`block truncate text-[11.5px] tabular-nums ${dark ? 'text-white/45' : 'text-neutral-500'}`} data-id="element-776" data-mp="fr8h8vz67gil">
                  {nodes.filter(n => n.surfaces.includes(s.id)).length} tools ·{' '}
                  {s.note}
                </span>
              </span>
            </button>
          </li>)}
      </ul>
    </div>;
}
/**
 * The label doubles as the spacer that vertically centres a shorter column
 * against a taller one — but ONLY side by side. Stacked on a phone there is no
 * neighbouring column to centre against, so that padding became a large empty
 * band above "AI surfaces" with nothing on the other side of it. The offset is
 * therefore applied as a media-query-scoped custom property and only consumed
 * from md up.
 */
function ListLabel({
  children,
  offset = 0,
  dark = false
}: {
  children: React.ReactNode;
  offset?: number;
  dark?: boolean;
}) {
  return <li className={`flex h-[var(--label-h)] items-start text-[10.5px] font-bold uppercase tracking-[0.12em] transition-colors duration-700 ease-out md:h-[calc(var(--label-h)+var(--label-offset))] ${dark ? 'text-white/45' : 'text-neutral-400'}`} style={{
    ['--label-h' as string]: `${LABEL_H}px`,
    ['--label-offset' as string]: `${offset}px`
  }} data-id="element-777" data-mp="21abu7648b5lf">
      {children}
    </li>;
}